// src/hooks/usePWA.ts
import { useState, useEffect } from 'react';

interface BeforeInstallPromptEvent extends Event {
    prompt: () => Promise<void>;
    userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let globalDeferredPrompt: BeforeInstallPromptEvent | null = null;

export const usePWA = () => {
    const [isStandalone, setIsStandalone] = useState(false);
    const [isChecking, setIsChecking] = useState(true);
    const [isInstallable, setIsInstallable] = useState(false);

    useEffect(() => {
        const checkStandalone = () =>
            window.matchMedia('(display-mode: standalone)').matches ||
            (window.navigator as any).standalone === true ||
            document.referrer.includes('android-app://') ||
            window.matchMedia('(display-mode: minimal-ui)').matches;

        const standalone = checkStandalone();
        setIsStandalone(standalone);

        if (!standalone && globalDeferredPrompt) {
            setIsInstallable(true);
        }

        setIsChecking(false);

        const handleBeforeInstallPrompt = (e: Event) => {
            e.preventDefault();
            globalDeferredPrompt = e as BeforeInstallPromptEvent;

            // Браузер выдал промпт — значит PWA точно не установлена
            setIsStandalone(false);
            setIsInstallable(true);

            // Сбрасываем старый флаг если был
            localStorage.removeItem('pwa_installed');
        };

        const handleAppInstalled = () => {
            setIsStandalone(true);
            setIsInstallable(false);
            globalDeferredPrompt = null;
            localStorage.setItem('pwa_installed', 'true');
        };

        window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
        window.addEventListener('appinstalled', handleAppInstalled);

        const interval = setInterval(() => {
            if (checkStandalone()) {
                setIsStandalone(true);
                setIsInstallable(false);
                globalDeferredPrompt = null;
                localStorage.setItem('pwa_installed', 'true');
            }
        }, 2000);

        return () => {
            window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
            window.removeEventListener('appinstalled', handleAppInstalled);
            clearInterval(interval);
        };
    }, []);

    const install = async () => {
        if (!globalDeferredPrompt) {
            showManualInstallInstructions();
            return null;
        }

        try {
            await globalDeferredPrompt.prompt();
            const { outcome } = await globalDeferredPrompt.userChoice;

            if (outcome === 'accepted') {
                globalDeferredPrompt = null;
                setIsStandalone(true);
                setIsInstallable(false);
                localStorage.setItem('pwa_installed', 'true');
            }

            return outcome;
        } catch (error) {
            console.error('Ошибка при установке:', error);
            return null;
        }
    };

    const showManualInstallInstructions = () => {
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
        const isAndroid = /Android/.test(navigator.userAgent);

        let message = '📱 Чтобы установить приложение:\n\n';
        if (isIOS) {
            message += '1. Нажмите кнопку "Поделиться" 📤\n2. Выберите "На экран «Домой»"\n3. Нажмите "Добавить"';
        } else if (isAndroid) {
            message += '1. Нажмите на меню браузера ⋮\n2. Выберите "Установить приложение"\n3. Нажмите "Установить"';
        } else {
            message += 'Нажмите на меню браузера и выберите "Установить приложение"';
        }

        alert(message);
    };

    // isInstalled = реально запущено в standalone режиме прямо сейчас
    // НЕ используем localStorage как источник правды
    const isInstalled = isStandalone;

    return {
        isInstalled,
        isStandalone,
        isChecking,
        isInstallable: isInstallable && !isStandalone,
        install,
    };
};